import { useRef, useState } from 'react';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useServiceSearch } from '@/billing/services/use-service-search';
import type { BillingServiceModel } from '@/shared/types/billing';
import { Search } from 'lucide-react';

/**
 * Service typeahead used by both the create banner and the inline edit row.
 *
 * PM parity: the duplicated `ServiceTypeahead` in `provider-fee-add-row.tsx`
 * and `provider-fee-edit-row.tsx`. Extracted to one shared component here.
 * Recomposed on DS: PM's lucide `Search` → DS `Icon name="search"`. DS has no
 * inline `close`/`x` glyph in the allowed registry (close is explicitly
 * forbidden by the port brief), so the circular × clear button is composed
 * from a styled button with an HTML entity — see report DS-GAP. No hardcoded
 * hex (token-backed utility classes only).
 *
 * Matches Flutter: the Provider Fees typeahead does NOT scope the search by
 * providerId (`useCaseParam` is unset on the SearchPicker) — it searches the
 * whole service catalog.
 */

interface ServiceTypeaheadProps {
  value: string;
  onTypingChange: (v: string) => void;
  onSelect: (svc: BillingServiceModel) => void;
  onClear: () => void;
  /**
   * Seeds the search (and focuses the input so it runs) — the wrap-up Pending orders "Choose
   * service" path opens the picker pre-filtered by the order's code (PROD-2594). The host should
   * pass the same string as `value`.
   */
  initialQuery?: string;
}

export function ServiceTypeahead({
  value,
  onTypingChange,
  onSelect,
  onClear,
  initialQuery,
}: ServiceTypeaheadProps) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(initialQuery ?? '');
  const debouncedQuery = useDebounce(query, 250);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { data, isFetching } = useServiceSearch({
    query: debouncedQuery,
    enabled: focused,
  });
  const results = data?.results ?? [];
  const showDropdown =
    focused && debouncedQuery.trim().length >= 2 && results.length > 0;

  return (
    <div className="relative">
      <span
        className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint pointer-events-none"
        aria-hidden
      >
        <Search className="size-4" aria-label="Search" />
      </span>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search CPT code or name"
        value={value}
        autoFocus={!!initialQuery}
        onChange={(e) => {
          setQuery(e.target.value);
          onTypingChange(e.target.value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => window.setTimeout(() => setFocused(false), 150)}
        aria-label="Search service to add"
        className="w-full h-10 pl-10 pr-10 text-sm bg-grey-100 rounded-md placeholder:text-text-hint focus:outline-none focus:ring-2 focus:ring-brand-primary"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onClear();
            setQuery('');
            inputRef.current?.focus();
          }}
          aria-label="Clear service"
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white text-xs leading-none hover:bg-interactive-hover"
        >
          &times;
        </button>
      )}
      {focused && isFetching && debouncedQuery.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-surface-card border border-border-default rounded-md shadow-md p-3 text-xs text-text-muted">
          Searching…
        </div>
      )}
      {showDropdown && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1 z-30 max-h-60 overflow-y-auto bg-surface-card border border-border-default rounded-md shadow-md py-1"
        >
          {results.slice(0, 50).map((svc, i) => {
            const display = svc.procedureCode?.displayName ?? '';
            const code = svc.procedureCode?.code ?? '';
            return (
              <li key={`${svc.id ?? code}-${i}`}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(svc);
                    setQuery('');
                    setFocused(false);
                    inputRef.current?.blur();
                  }}
                  className="w-full text-left px-3 py-1.5 hover:bg-grey-100 flex flex-col gap-1"
                >
                  <span className="text-sm text-text-primary truncate">
                    {display || code}
                  </span>
                  {code && display && (
                    <span className="text-xs text-text-muted tabular-nums">{code}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
